import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiErrorCodes, parseApiResponseError } from '../../../../api/api-error';
import apiManager from '../../../../api/api-manager';
import { RecuperoPasswordError } from '../../../../api/generated';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  RetrievePasswordError,
  RetrievePasswordParams,
  RetrievePasswordState,
} from './retrieve-password-types';

const initialState: RetrievePasswordState = {
  retrievePasswordRequestStatus: 'idle',
};

export const retrievePassword = createAsyncThunk<
  string,
  RetrievePasswordParams,
  AsyncThunkConfig<RetrievePasswordError>
>('retrieve password', async (params: RetrievePasswordParams, thunkApi) => {
  try {
    if (!params.email) {
      return thunkApi.rejectWithValue('USERNAME_REQUIRED');
    }

    await apiManager.loginApi.recuperaPassword({
      username: params.email,
      sendTo: params.selectedOption,
    });
    return null;
  } catch (error) {
    const apiError = parseApiResponseError<RecuperoPasswordError>(error);
    const errorCodes = apiError.payload?.codiciErrore;
    if (apiError.responseErrorCode === ApiErrorCodes.TOO_MANY_REQUESTS) {
      return thunkApi.rejectWithValue('TOO_MANY_REQUESTS');
    } else if (errorCodes && errorCodes.indexOf('UTENZA_NON_MIGRATA') >= 0) {
      return thunkApi.rejectWithValue('NOT_MIGRATED_USER');
    } else if (errorCodes && errorCodes.indexOf('CODICE_UTENTE_ERRATO') >= 0) {
      return thunkApi.rejectWithValue('INVALID_USER_CODE');
    } else {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
});

const retrievePasswordSlice = createSlice({
  name: 'features/retrieve-password',
  initialState,
  reducers: {
    resetRetrievePasswordState(state) {
      state.retrievePasswordError = undefined;
      state.retrievePasswordRequestStatus = 'idle';
    },
    clearRetrievePasswordError(state) {
      state.retrievePasswordError = undefined;
      state.retrievePasswordRequestStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(retrievePassword.pending, (state) => {
      state.retrievePasswordRequestStatus = 'loading';
    });
    builder.addCase(retrievePassword.fulfilled, (state) => {
      state.retrievePasswordRequestStatus = 'succeeded';
    });
    builder.addCase(retrievePassword.rejected, (state, action) => {
      state.retrievePasswordError = action.payload;
      state.retrievePasswordRequestStatus = 'failed';
    });
  },
});

export const { resetRetrievePasswordState, clearRetrievePasswordError } =
  retrievePasswordSlice.actions;

export default retrievePasswordSlice.reducer;
